import { UniformBufferBlock } from "./UniformBufferBlock";
import { roundUp } from "./UniformBufferManager";
export class UniformBufferCluster {
    constructor(blockSize, blockNum, manager) {
        this._inManagerUpdateArray = false;
        this._sn = 0;
        this._id = 0;
        this._destroyed = false;
        this._blocks = [];
        this._holeNum = 0;
        this._expand = 16;
        this._id = UniformBufferCluster._idCounter++;
        this.manager = manager;
        this._blockSize = blockSize;
        this._blockNum = blockNum;
        this._totalSize = blockSize * blockNum;
        this._needUpload = new Array(blockNum).fill(false);
        this.data = new ArrayBuffer(this._totalSize);
        this._move = new Uint8Array(this._blockSize);
        this.buffer = this.manager.createGPUBuffer(this._totalSize);
        this.manager.statisGPUMemory(this._totalSize);
    }
    get usedNum() {
        return this._blocks.length;
    }
    _expandBuffer() {
        let expandNum = this._blockNum;
        this._blockNum += this._expand;
        if (this._blockNum > this.manager.clusterMaxBlock)
            this._blockNum = this.manager.clusterMaxBlock;
        expandNum = this._blockNum - expandNum;
        if (expandNum < 1)
            return false;
        this._totalSize = this._blockSize * this._blockNum;
        const expandSize = this._blockSize * this._expand;
        this._needUpload = this._needUpload.concat(new Array(expandNum).fill(false));
        const newArrayBuffer = new ArrayBuffer(this._totalSize);
        new Uint8Array(newArrayBuffer).set(new Uint8Array(this.data));
        this.data = newArrayBuffer;
        this.buffer = this.manager.createGPUBuffer(this._totalSize, null, this.data);
        this.manager.statisGPUMemory(expandSize);
        this._blocks.forEach(block => block && block.user.notifyGPUBufferChange('expand'));
        return true;
    }
    _moveBlock(index) {
        const len = this._blocks.length;
        if (index >= len)
            return false;
        const dataView = new Uint8Array(this.data);
        const size = this._blockSize;
        for (let i = index + 1; i < len; i++) {
            const start = i * size;
            const end = start + size;
            const target = start - size;
            dataView.copyWithin(target, start, end);
            this._needUpload[i - 1] = this._needUpload[i];
            this._blocks[i - 1] = this._blocks[i];
            if (this._blocks[i - 1]) {
                this._blocks[i - 1].index--;
                this._blocks[i - 1].offset -= size;
                this._blocks[i - 1].user.notifyGPUBufferChange('moveBlock');
            }
        }
        this._blocks.length--;
        return true;
    }
    _createBufferBlock(index, size, alignedSize, user) {
        return new UniformBufferBlock(this, index, size, alignedSize, user);
    }
    getBlock(size, user) {
        const alignedSize = roundUp(size, this.manager.byteAlign);
        if (alignedSize !== this._blockSize) {
            console.warn('WebGPUBufferCluster: 获取内存块时, 长度错误!');
            return null;
        }
        const index = this._getBlockWithExpand();
        const bb = this._createBufferBlock(index, size, alignedSize, user);
        this._blocks[index] = bb;
        return bb;
    }
    freeBlock(bb) {
        const index = this._blocks.indexOf(bb);
        if (index !== -1) {
            if (index === this._blocks.length - 1)
                this._blocks.length--;
            else {
                this._blocks[index] = null;
                this._holeNum++;
            }
            bb.destroy();
            if (this._holeNum > this.manager.removeHoleThreshold) {
                this.manager._addRemoveHoleCluster(this);
                this._holeNum = 0;
            }
            return true;
        }
        return false;
    }
    upload() {
        var _a;
        let count = 0;
        let bytes = 0;
        let next = false;
        let startIndex = -1;
        let endIndex = -1;
        let offset = 0;
        let size = 0;
        for (let i = 0, len = this._blocks.length; i < len; i++) {
            if (this._needUpload[i]) {
                if (startIndex === -1)
                    startIndex = i;
                endIndex = i;
                next = true;
                this._needUpload[i] = false;
                (_a = this._blocks[i]) === null || _a === void 0 ? void 0 : _a.user.updateOver();
            }
            else {
                if (next) {
                    offset = startIndex * this._blockSize;
                    size = (endIndex - startIndex + 1) * this._blockSize;
                    this.manager.writeBuffer(this.buffer, this.data, offset, size);
                    count++;
                    bytes += size;
                    startIndex = -1;
                    endIndex = -1;
                    next = false;
                }
            }
        }
        if (next) {
            offset = startIndex * this._blockSize;
            size = (endIndex - startIndex + 1) * this._blockSize;
            this.manager.writeBuffer(this.buffer, this.data, offset, size);
            count++;
            bytes += size;
        }
    }
    _addUploadBlock(index) {
        this._needUpload[index] = true;
        if (!this._inManagerUpdateArray)
            this.manager._addUpdateArray(this);
    }
    optimize() {
        let ret = false;
        for (let i = 0, len = this._blocks.length; i < len; i++) {
            const bb = this._blocks[i];
            if (bb && !bb.moved && bb.uploadNum > this.manager.uploadThreshold && i > 0) {
                const size = this._blockSize;
                const dataView = new Uint8Array(this.data);
                this._move.set(new Uint8Array(this.data, size * i, size));
                for (let j = i - 1; j >= 0; j--) {
                    const start = j * size;
                    const end = start + size;
                    const target = start + size;
                    dataView.copyWithin(target, start, end);
                    this._needUpload[j + 1] = this._needUpload[j];
                    this._blocks[j + 1] = this._blocks[j];
                    if (this._blocks[j + 1]) {
                        this._blocks[j + 1].index++;
                        this._blocks[j + 1].offset += size;
                        this._blocks[j + 1].user.notifyGPUBufferChange('optimize');
                    }
                }
                dataView.set(this._move);
                bb.index = 0;
                bb.offset = 0;
                bb.moved = true;
                this._blocks[0] = bb;
                this._blocks[0].user.notifyGPUBufferChange('optimize');
                ret = true;
            }
        }
        return ret;
    }
    removeHole() {
        let ret = false;
        for (let i = this._blocks.length - 1; i > -1; i--) {
            if (!this._blocks[i]) {
                if (this._moveBlock(i)) {
                    ret = true;
                }
            }
        }
        this._holeNum = 0;
        return ret;
    }
    clear(blockNum) {
        this._blocks.forEach(block => block && block.destroy());
        this._blocks.length = 0;
        if (blockNum != undefined && blockNum > 0 && blockNum !== this._blockNum) {
            this._blockNum = blockNum;
            this._totalSize = this._blockSize * this._blockNum;
            this.buffer = this.manager.createGPUBuffer(this._totalSize);
            this.data = new ArrayBuffer(this._totalSize);
        }
        else {
            this._blockNum = 0;
            this._totalSize = 0;
            this.buffer = null;
            this.data = null;
        }
        this._needUpload.length = this._blockNum;
        this._needUpload.fill(false);
    }
    _getBlockWithExpand() {
        for (let i = this._blocks.length - 1; i > -1; i--) {
            if (!this._blocks[i]) {
                this._holeNum--;
                return i;
            }
        }
        if (this._blocks.length < this._blockNum)
            return this._blocks.length;
        else {
            this._expandBuffer();
            return this._blocks.length;
        }
    }
    destroy() {
        var _a;
        if (!this._destroyed) {
            this.clear();
            (_a = this.buffer.destroy) !== null && _a !== void 0 ? _a : this.buffer.destroy();
            this.manager.statisGPUMemory(-this._totalSize);
            this._destroyed = true;
            return true;
        }
        console.warn('UniformBufferCluster: object alreay destroyed!');
        return false;
    }
}
UniformBufferCluster._idCounter = 0;
